import React from 'react';

import { FaTachometerAlt, FaUsers, FaRegCalendarAlt, FaCog, FaEnvelope, FaSignOutAlt, FaSearch, FaBell, FaDollarSign, FaFileAlt, FaStar } from "react-icons/fa";
import { NavLink } from "react-router-dom";
import { useNavigate } from 'react-router-dom';

// This is your Sidebar component, slightly refined to match the image styles
const Sidebar = () => {
  const menuItems = [
    { name: "Dashboard", icon: <FaTachometerAlt />, path: "/dashboard" },
    { name: "Requests", icon: <FaEnvelope />, path: "/requests" },
    { name: "Users", icon: <FaUsers />, path: "/users" },
    { name: "Events", icon: <FaRegCalendarAlt />, path: "/events" },
    { name: "Settings", icon: <FaCog />, path: "/settings" },
  ];
  const navigate = useNavigate()

  return (
    // The `sticky` and `top-0` classes make the sidebar stick to the top.
    // `h-screen` makes it take the full height of the viewport.
    <aside className="w-64 bg-white shadow-md flex flex-col justify-between p-4 sticky top-0 h-screen">
      <div>
        <h1 className="text-2xl font-bold text-gray-800 mb-8 px-2">AdminPanel</h1>
        <nav>
          <ul>
            {menuItems.map((item) => (
              <li key={item.name} className="mb-2">
                <NavLink
                  to={item.path}
                  end // Use `end` prop for the root path to avoid it being active for all child routes
                  className={({ isActive }) =>
                    `flex items-center gap-3 p-3 rounded-lg text-gray-600 hover:bg-indigo-100 hover:text-indigo-700 transition-colors ${isActive ? "bg-indigo-500 text-white font-semibold shadow" : ""
                    }`
                  }
                >
                  {item.icon}
                  <span>{item.name}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </div>
      <div className="p-2" >
        <button  onClick={()=>{navigate('/')}} className="flex items-center gap-3 p-3 text-gray-600 hover:bg-gray-100 rounded-lg w-full text-left">
          <FaSignOutAlt />
          <span>Logout</span>
        </button>
      </div>
    </aside>
  );
};

// Reusable components for the dashboard content
const StatCard = ({ icon, value, label, percentage, color }) => (
  <div className="bg-white p-6 rounded-xl shadow-sm flex items-start justify-between">
    <div>
      <div className={`p-3 rounded-lg bg-${color}-100 text-${color}-600 inline-block`}>
        {icon}
      </div>
      <p className="text-3xl font-bold mt-4">{value}</p>
      <p className="text-gray-500">{label}</p>
    </div>
    <span className={`text-sm font-semibold text-green-500`}>{percentage}</span>
  </div>
);

const StatusBadge = ({ status }) => {
  const statusStyles = {
    Pending: 'bg-yellow-100 text-yellow-800',
    Approved: 'bg-green-100 text-green-800',
    Rejected: 'bg-red-100 text-red-800',
    Active: 'bg-green-100 text-green-800',
    Inactive: 'bg-gray-100 text-gray-800',
  };
  return (
    <span className={`px-2 py-1 text-xs font-semibold leading-tight rounded-full ${statusStyles[status]}`}>
      {status}
    </span>
  );
};

// The main content area of the dashboard
const DashboardContent = () => {
  const mentorshipRequests = [
    { id: 'REQ001', student: 'TechStart Solutions', type: 'Funding', date: '2024-02-15', status: 'Pending' },
    { id: 'REQ002', student: 'Green Innovate', type: 'Mentorship', date: '2024-02-14', status: 'Approved' },
    { id: 'REQ003', student: 'DataFlow AI', type: 'Office Space', date: '2024-02-13', status: 'Rejected' },
    { id: 'REQ004', student: 'HealthTech Pro', type: 'Funding', date: '2024-02-12', status: 'Pending' },
  ];
  const assignedStudents = [
    { id: 'USR001', name: 'John Smith', role: 'Startup Founder', email: 'john@example.com', status: 'Active' },
    { id: 'USR002', name: 'Sarah Johnson', role: 'Mentor', email: 'sarah@example.com', status: 'Active' },
    { id: 'USR003', name: 'Mike Wilson', role: 'Startup Founder', email: 'mike@example.com', status: 'Inactive' },
    { id: 'USR004', name: 'Emily Brown', role: 'Mentor', email: 'emily@example.com', status: 'Active' },
  ];
  const scheduledSessions = [
    { name: 'Startup Pitch Day', date: '2024-02-20', host: 'Innovation Hub', desc: 'Annual startup pitch competition' },
    { name: 'Mentorship Workshop', date: '2024-02-22', host: 'Tech Mentors', desc: 'Workshop on effective mentoring' },
    { name: 'Investor Meetup', date: '2024-02-25', host: 'Venture Capital Group', desc: 'Networking event for investors' },
    { name: 'Startup Clinic', date: '2024-02-28', host: 'Business Experts', desc: 'One-on-one business consultation' },
  ];

  return (
    <main className="flex-1 bg-gray-50 p-8">
      <header className="flex justify-between items-center mb-8">
        <h2 className="text-3xl font-bold text-gray-800">Dashboard Overview</h2>
        <div className="flex items-center space-x-6">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" placeholder="Search" className="pl-10 pr-4 py-2 rounded-lg border bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>
          <div className="relative">
            <FaBell className="text-gray-600" />
            <span className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full border-2 border-white"></span>
          </div>
          <div className="flex items-center space-x-3">
            <img src="https://i.pravatar.cc/40" alt="User" className="rounded-full" />
            <div>
              <p className="font-semibold text-gray-800">John Doe</p>
              <p className="text-sm text-gray-500">Admin</p>
            </div>
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard icon={<FaUsers />} value="24,832" label="Active Users" percentage="+12%" color="indigo" />
        <StatCard icon={<FaDollarSign />} value="$142,384" label="Total Revenue" percentage="+8%" color="blue" />
        <StatCard icon={<FaFileAlt />} value="1,240" label="Pending Requests" percentage="+15%" color="purple" />
        <StatCard icon={<FaStar />} value="18,453" label="New Users" percentage="+5%" color="pink" />
      </div>

      <div className="space-y-8">
        <div className="bg-white p-6 rounded-xl shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold text-gray-800">Mentorship Requests</h3>
            <a href="#" className="text-sm text-indigo-600 hover:underline">View All</a>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="border-b-2 border-gray-100">
                <tr>
                  <th className="py-3 px-4 font-semibold text-sm text-gray-500">Request ID</th>
                  <th className="py-3 px-4 font-semibold text-sm text-gray-500">Student Name</th>
                  <th className="py-3 px-4 font-semibold text-sm text-gray-500">Type</th>
                  <th className="py-3 px-4 font-semibold text-sm text-gray-500">Date</th>
                  <th className="py-3 px-4 font-semibold text-sm text-gray-500">Status</th>
                </tr>
              </thead>
              <tbody>
                {mentorshipRequests.map(req => (
                  <tr key={req.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="py-4 px-4 text-gray-700">{req.id}</td>
                    <td className="py-4 px-4 text-gray-900 font-medium">{req.student}</td>
                    <td className="py-4 px-4 text-gray-700">{req.type}</td>
                    <td className="py-4 px-4 text-gray-700">{req.date}</td>
                    <td className="py-4 px-4"><StatusBadge status={req.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </main>
  );
};

// This component combines the Sidebar and the main content into a single layout
const AdminLayout = () => {
  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
    </div>
  );
};

// You would use this AdminLayout as the main component for your authenticated routes.
export default AdminLayout;
